import { Users } from '../models/User.tsx';
import { getLiteQueryDocs } from './firestoreLiteQuery.ts';
import { where as liteWhere } from 'firebase/firestore/lite';

const FIRESTORE_IN_LIMIT = 10;

export type HierarchyViewer = Pick<Users, 'userName' | 'userType'>;

export type HierarchyRoster = {
  subAgents: Users[];
  players: Users[];
  /** Parent usernames that own the players in this roster. */
  agentScope: string[];
};

type CacheEntry = {
  value: HierarchyRoster;
  fetchedAt: number;
};

const rosterCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<HierarchyRoster>>();

export function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

function buildCacheKey(viewer: HierarchyViewer): string {
  return `${viewer.userType}:${viewer.userName}`;
}

function mapUserDocs(docs: { id: string; data: () => Users }[]): Users[] {
  return docs.map(d => ({
    id: d.id,
    ...(d.data() as Users),
  }));
}

async function fetchPlayersForParents(parentNames: string[]): Promise<Users[]> {
  if (parentNames.length === 0) return [];

  const chunks = chunkArray(parentNames, FIRESTORE_IN_LIMIT);
  const snapshots = await Promise.all(
    chunks.map(chunk =>
      getLiteQueryDocs('users', [
        liteWhere('parent', 'in', chunk),
        liteWhere('userType', '==', 'Player'),
      ])
    )
  );

  return snapshots.flatMap(docs => mapUserDocs(docs));
}

async function fetchRosterUncached(viewer: HierarchyViewer): Promise<HierarchyRoster> {
  const userType = viewer.userType;

  if (userType === 'subAgent') {
    const players = await fetchPlayersForParents([viewer.userName]);
    return {
      subAgents: [],
      players,
      agentScope: [viewer.userName],
    };
  }

  if (userType === 'masterAgent') {
    const subAgentsDocs = await getLiteQueryDocs('users', [
      liteWhere('parent', '==', viewer.userName),
      liteWhere('userType', '==', 'subAgent'),
    ]);
    const subAgents = mapUserDocs(subAgentsDocs);
    const agentScope = subAgents.map(a => a.userName);
    const players = await fetchPlayersForParents(agentScope);
    return { subAgents, players, agentScope };
  }

  return { subAgents: [], players: [], agentScope: [] };
}

/**
 * Resolves visible subAgents + players for a master/sub agent with in-memory
 * session cache and in-flight dedupe. Cache dies on full page refresh.
 * Call invalidateHierarchyRoster on user-tree mutations / logout
 * (also wired from requestAgentSummaryRefresh).
 */
export async function resolveHierarchyRoster(
  viewer: HierarchyViewer,
  options: { ttlMs?: number; force?: boolean } = {}
): Promise<HierarchyRoster> {
  // Default: keep for the whole SPA session (until refresh / invalidate).
  const ttlMs = options.ttlMs ?? Number.POSITIVE_INFINITY;
  const key = buildCacheKey(viewer);
  const now = Date.now();

  if (!options.force) {
    const cached = rosterCache.get(key);
    if (cached && now - cached.fetchedAt < ttlMs) {
      return cached.value;
    }

    const pending = inflight.get(key);
    if (pending) return pending;
  }

  const promise = fetchRosterUncached(viewer)
    .then(value => {
      rosterCache.set(key, { value, fetchedAt: Date.now() });
      return value;
    })
    .catch(error => {
      const code = (error as { code?: string })?.code;
      // Common when auth is cleared mid-flight (session expire / logout).
      if (code !== "permission-denied") {
        console.error("Error resolving hierarchy roster:", error);
      }
      return { subAgents: [], players: [], agentScope: [] } as HierarchyRoster;
    })
    .finally(() => {
      if (inflight.get(key) === promise) {
        inflight.delete(key);
      }
    });

  // Always register in-flight work so concurrent callers join the same fetch.
  const raced = inflight.get(key);
  if (!options.force && raced) {
    return raced;
  }
  inflight.set(key, promise);
  return promise;
}

export function invalidateHierarchyRoster(viewer?: HierarchyViewer): void {
  if (viewer) {
    const key = buildCacheKey(viewer);
    rosterCache.delete(key);
    inflight.delete(key);
    return;
  }
  rosterCache.clear();
  inflight.clear();
}

/** Keep the session roster in sync after in-place user edits (pin, limits, etc.). */
export function patchHierarchyRosterUser(userId: string, updates: Partial<Users>): void {
  if (!userId) return;

  const patchList = (list: Users[]) =>
    list.map(user => (user.id === userId ? { ...user, ...updates } : user));

  rosterCache.forEach((entry, key) => {
    rosterCache.set(key, {
      ...entry,
      value: {
        ...entry.value,
        players: patchList(entry.value.players),
        subAgents: patchList(entry.value.subAgents),
      },
    });
  });
}

export function getVisibleUserNamesFromRoster(roster: HierarchyRoster): string[] {
  return [
    ...roster.subAgents.map(a => a.userName),
    ...roster.players.map(p => p.userName),
  ];
}

export function getCustomerUserNamesFromRoster(
  roster: HierarchyRoster,
  includeSubAgents: boolean
): string[] {
  const names = roster.players.map(p => p.userName);
  if (includeSubAgents) {
    names.push(...roster.subAgents.map(a => a.userName));
  }
  return names;
}
